import { useEffect, useState } from "react";
import { api, photoUrl } from "@/lib/api";
import { Car, CalendarDays, MapPin, Mail, Phone, X, StickyNote, Check, Ban, Send, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { statusClass } from "./AdminDashboard";
import { sizeLabel } from "@/lib/pricing";
import PublishShowcase from "@/components/PublishShowcase";
import QuoteForm from "@/components/QuoteForm";
import BookingLocationMap from "@/components/BookingLocationMap";
import OnMyWayButton from "@/components/OnMyWayButton";
import NewBookingModal from "@/components/NewBookingModal";

const STATUSES = ["waitlisted", "pending", "awaiting_deposit", "deposit_paid", "confirmed", "in_progress", "completed", "cancelled", "declined", "expired"];

export default function AdminBookings() {
  const [filter, setFilter] = useState("");
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    const { data } = await api.get("/bookings", {
      params: filter ? { status: filter } : {},
    });
    setBookings(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const updateStatus = async (bookingId, status) => {
    await api.patch(`/bookings/${bookingId}/status`, { status });
    await load();
    if (selected?.id === bookingId) {
      const { data } = await api.get(`/bookings/${bookingId}`);
      setSelected(data);
    }
  };

  const waitlistDecision = async (bookingId, action) => {
    try {
      const { data } = await api.patch(`/bookings/${bookingId}/waitlist`, { action });
      toast.success(action === "approve" ? "Approved — customer emailed a payment link" : "Declined — customer notified");
      setSelected(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const sendDepositLink = async (bookingId) => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/send-deposit-link`);
      toast.success("Deposit link emailed — booking confirms once they pay");
      if (selected?.id === bookingId) setSelected(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send deposit link");
    }
  };

  const sendBalanceLink = async (bookingId) => {
    try {
      const { data } = await api.post(`/bookings/${bookingId}/send-balance-link`);
      toast.success("Balance payment link emailed to the customer");
      if (selected?.id === bookingId) setSelected(data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send balance link");
    }
  };

  const declineBooking = async (bookingId) => {
    await updateStatus(bookingId, "declined");
    toast("Booking marked as declined");
  };

  const deleteBooking = async (bookingId) => {
    try {
      await api.delete(`/bookings/${bookingId}`);
      toast.success("Booking permanently deleted");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete booking");
    }
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Bookings</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">
            All appointments
          </h1>
        </div>
        <button
          onClick={() => setShowNew(true)}
          data-testid="admin-new-booking-button"
          className="cj-btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> New booking
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("")}
          data-testid="admin-booking-filter-all"
          className={`px-3 py-1.5 rounded-full text-xs font-display uppercase font-bold tracking-wider border ${
            filter === "" ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]" : "bg-[#121824] text-slate-300 border-white/10"
          }`}
        >
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`admin-booking-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-display uppercase font-bold tracking-wider border ${
              filter === s ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00]" : "bg-[#121824] text-slate-300 border-white/10"
            }`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {bookings.length === 0 && (
          <div className="cj-card p-6 text-slate-400 text-sm">No bookings in this view.</div>
        )}
        {bookings.map((b) => (
          <div
            key={b.id}
            className="cj-card p-5 flex flex-wrap items-center justify-between gap-4 cursor-pointer"
            onClick={() => setSelected(b)}
            data-testid={`booking-row-${b.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-display uppercase font-extrabold text-lg text-slate-100 truncate">
                  {b.customer.name}
                </div>
                <span className={`cj-pill border ${statusClass(b.status)}`}>
                  {b.status.replace("_", " ")}
                </span>
                {b.payment_status === "paid" && (
                  <span className="cj-pill bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                    Deposit ${b.deposit_amount}
                  </span>
                )}
              </div>
              <div className="font-mono text-xs text-slate-500 mt-2 flex flex-wrap gap-x-5 gap-y-1">
                <span className="flex items-center gap-1">
                  <Car className="h-3 w-3" /> {b.vehicle.year} {b.vehicle.make} {b.vehicle.model}
                </span>
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" /> {b.scheduled_date} · {b.scheduled_time}
                </span>
                <span className="truncate max-w-sm flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {b.customer.address}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {["pending", "awaiting_deposit"].includes(b.status) && b.payment_status !== "paid" && (
                <button
                  onClick={(e) => { e.stopPropagation(); sendDepositLink(b.id); }}
                  data-testid={`quick-send-link-${b.id}`}
                  className="cj-btn-primary px-4 py-2 rounded-full text-xs inline-flex items-center gap-1 whitespace-nowrap"
                >
                  <Send className="h-3.5 w-3.5" /> {b.status === "awaiting_deposit" ? "Resend link" : "Confirm & send link"}
                </button>
              )}
              <div className="font-display font-extrabold text-[#ff6b00]">
                {b.service_name}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <BookingDetailDrawer
          booking={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(st) => updateStatus(selected.id, st)}
          onWaitlist={(action) => waitlistDecision(selected.id, action)}
          onSendDepositLink={() => sendDepositLink(selected.id)}
          onSendBalanceLink={() => sendBalanceLink(selected.id)}
          onDecline={() => declineBooking(selected.id)}
          onDelete={() => deleteBooking(selected.id)}
          onQuoteSent={(b) => { setSelected(b); load(); }}
        />
      )}

      {showNew && (
        <NewBookingModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}

function BookingDetailDrawer({ booking, onClose, onStatusChange, onWaitlist, onSendDepositLink, onSendBalanceLink, onDecline, onDelete, onQuoteSent }) {
  const [showDecline, setShowDecline] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const canReview = ["pending", "awaiting_deposit"].includes(booking.status) && booking.payment_status !== "paid";
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose} data-testid="booking-detail-drawer">
      <div
        className="w-full sm:max-w-xl bg-[#0b0f17] border-l border-white/10 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0b0f17]/95 backdrop-blur-xl border-b border-white/10 flex items-center justify-between p-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Booking</div>
            <div className="font-display uppercase font-extrabold text-lg text-slate-100">
              {booking.customer.name}
            </div>
          </div>
          <button onClick={onClose} data-testid="booking-detail-close" className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {booking.status === "waitlisted" && (
            <div className="rounded-xl border border-purple-500/30 bg-purple-500/5 p-4" data-testid="waitlist-decision-card">
              <div className="font-display uppercase font-bold text-purple-200">Waitlist request · {booking.distance_km ?? "?"} km away</div>
              <div className="text-xs text-slate-300 mt-1">No deposit taken. Approving emails the customer a link to pay the ${booking.deposit_amount} deposit.</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => onWaitlist("approve")} data-testid="waitlist-approve-button" className="cj-btn-primary flex-1 px-4 py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-1">
                  <Check className="h-4 w-4" /> Approve
                </button>
                <button onClick={() => onWaitlist("decline")} data-testid="waitlist-decline-button" className="flex-1 px-4 py-2.5 rounded-full text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center justify-center gap-1">
                  <Ban className="h-4 w-4" /> Decline
                </button>
              </div>
            </div>
          )}

          {canReview && (
            <div className="cj-card p-4 space-y-3" data-testid="booking-actions-card">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Review this request</div>
              <div className="text-xs text-slate-400">
                {booking.status === "awaiting_deposit"
                  ? `Deposit link already sent. This booking confirms automatically once the customer pays the $${booking.deposit_amount} deposit.`
                  : `Confirm to email the customer a $${booking.deposit_amount} deposit link. The booking is confirmed when they pay.`}
              </div>
              <div className="flex gap-2">
                <button onClick={onSendDepositLink} data-testid="send-deposit-link-button" className="cj-btn-primary flex-1 px-4 py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-1">
                  <Send className="h-4 w-4" /> {booking.status === "awaiting_deposit" ? "Resend deposit link" : "Confirm & send deposit link"}
                </button>
                <button onClick={() => setShowDecline((v) => !v)} data-testid="decline-booking-button" className="flex-1 px-4 py-2.5 rounded-full text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center justify-center gap-1">
                  <Ban className="h-4 w-4" /> Decline
                </button>
              </div>
              {showDecline && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4" data-testid="decline-panel">
                  <div className="text-xs text-slate-300">Give the customer a quick call to let them know — no email is sent automatically when you decline.</div>
                  <a href={`tel:${booking.customer.phone}`} data-testid="decline-call-link" className="mt-3 flex items-center gap-2 font-display font-extrabold text-xl text-[#ff6b00]">
                    <Phone className="h-5 w-5" /> {booking.customer.phone || "No phone on file"}
                  </a>
                  <button onClick={onDecline} data-testid="confirm-decline-button" className="mt-3 w-full px-4 py-2.5 rounded-full text-sm text-red-300 border border-red-500/40 hover:bg-red-500/10 inline-flex items-center justify-center gap-1">
                    <Ban className="h-4 w-4" /> Mark as declined
                  </button>
                </div>
              )}
            </div>
          )}

          {booking.status === "completed" && !booking.balance_paid && (booking.final_balance || 0) > 0 && (
            <div className="cj-card p-4 border border-[#ff6b00]/20" data-testid="balance-link-card">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Outstanding balance</div>
              <div className="text-xs text-slate-400 mt-1">
                A secure Stripe balance link was emailed automatically when you completed this job. Resend it if needed.
              </div>
              <button onClick={onSendBalanceLink} data-testid="send-balance-link-button" className="cj-btn-primary mt-3 w-full px-4 py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-1">
                <Send className="h-4 w-4" /> {booking.balance_link_sent_at ? "Resend balance payment link" : "Send balance payment link"}
              </button>
            </div>
          )}
          {booking.status === "completed" && booking.balance_paid && (
            <div className="cj-card p-4 border border-emerald-500/20 text-sm text-emerald-300" data-testid="balance-paid-note">
              ✓ Balance paid in full
            </div>
          )}

          <div className="cj-card p-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">
              Status
            </div>
            <select
              value={booking.status}
              onChange={(e) => onStatusChange(e.target.value)}
              data-testid="booking-detail-status-select"
              className="w-full bg-[#0f1522] border border-white/10 rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:border-[#ff6b00]"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace("_", " ")}
                </option>
              ))}
            </select>
          </div>

          {(booking.quote_only || booking.quote_status) && <QuoteForm key={booking.id + booking.quote_status} booking={booking} onSent={onQuoteSent} />}

          <Section title="Customer">
            <Line icon={Mail} value={booking.customer.email} />
            <Line icon={Phone} value={booking.customer.phone} />
            <Line icon={MapPin} value={booking.customer.address} />
          </Section>

          <BookingLocationMap key={booking.id} booking={booking} />

          {["pending", "deposit_paid", "confirmed", "in_progress"].includes(booking.status) && (
            <div className="flex items-center justify-between gap-3 cj-card p-4" data-testid="drawer-on-my-way">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Heading there now?</div>
                <div className="text-xs text-slate-400 mt-0.5">{booking.on_my_way_at ? `ETA sent (${booking.on_my_way_eta} min)` : "Email the customer your ETA from base"}</div>
              </div>
              <OnMyWayButton key={booking.id} bookingId={booking.id} customerName={booking.customer.name} alreadyAt={booking.on_my_way_at} />
            </div>
          )}

          <Section title="Vehicle">
            <Line icon={Car} value={`${booking.vehicle.year} ${booking.vehicle.make} ${booking.vehicle.model}`} />
            <Line value={`Color: ${booking.vehicle.color || "—"} · Size: ${sizeLabel(booking.vehicle.size)}`} />
          </Section>

          <Section title="Appointment">
            <Line icon={CalendarDays} value={`${booking.scheduled_date} · ${booking.scheduled_time}`} />
            <Line value={`Service: ${booking.service_name} ($${booking.service_price})${booking.quote_only ? " · QUOTE ONLY — confirm final price" : ""}`} />
            {booking.prerequisite_accepted && <Line value="✓ Customer confirmed garage / booking requirement" />}
            {Object.keys(booking.reminders_sent || {}).length > 0 && (
              <Line value={`Reminders sent (email): ${Object.keys(booking.reminders_sent).join(", ")}`} />
            )}
            {booking.addons?.length > 0 && (
              <Line value={`Add-ons: ${booking.addons.map((a) => `${a.name} (+$${a.price})`).join(", ")}`} />
            )}
            <Line value={`Deposit: $${booking.deposit_amount} · ${booking.payment_status} · Balance $${booking.final_balance?.toFixed(2)}`} />
          </Section>

          {booking.notes && (
            <Section title="Notes">
              <Line icon={StickyNote} value={booking.notes} />
            </Section>
          )}

          {booking.photos?.length > 0 && (
            <div className="cj-card p-4">
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">
                Photos ({booking.photos.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {booking.photos.map((p) => (
                  <a
                    key={p.id}
                    href={photoUrl(p.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="block aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#1a2234]"
                    data-testid={`booking-photo-${p.id}`}
                  >
                    <img src={photoUrl(p.id)} alt="car" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <PublishShowcase key={booking.id} booking={booking} />

          <div className="cj-card p-4 border border-red-500/20" data-testid="danger-zone">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-red-400/80 mb-2">Danger zone</div>
            <div className="text-xs text-slate-400">Permanently delete this booking along with its photos and payment records. This can't be undone — use it to clear mistaken or test bookings.</div>
            {!showDelete ? (
              <button onClick={() => setShowDelete(true)} data-testid="delete-booking-button" className="mt-3 px-4 py-2.5 rounded-full text-sm text-red-300 border border-red-500/30 hover:bg-red-500/10 inline-flex items-center gap-1">
                <Trash2 className="h-4 w-4" /> Delete booking
              </button>
            ) : (
              <div className="mt-3 flex gap-2">
                <button onClick={onDelete} data-testid="confirm-delete-booking-button" className="flex-1 px-4 py-2.5 rounded-full text-sm text-red-200 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 inline-flex items-center justify-center gap-1">
                  <Trash2 className="h-4 w-4" /> Yes, delete permanently
                </button>
                <button onClick={() => setShowDelete(false)} data-testid="cancel-delete-booking-button" className="flex-1 px-4 py-2.5 rounded-full text-sm cj-btn-ghost">Keep it</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="cj-card p-4">
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">
        {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Line({ icon: Icon, value }) {
  return (
    <div className="flex items-start gap-2 text-sm text-slate-200">
      {Icon && <Icon className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />}
      <span>{value}</span>
    </div>
  );
}
